import React, { useState, useRef, useEffect } from 'react';
import { FaPlus, FaSave, FaTimes, FaArrowLeft } from 'react-icons/fa';
import LoadingSpinner from './partial/LoadingSpinner.jsx';
import FreeRequestsToggle from './partial/FreeRequestsToggle.jsx';
import '../styles/BandSettings.css';
import config from '../config.js';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { findSocialMediaLinkError, normalizeSocialMediaUrl } from '../_helpers/socialMediaLinks.js';
import { useT } from '../contexts/LocaleContext.jsx';

function BandSettingsForm({ band, onBack, onSuccess }) {
  const [bandData, setBandData] = useState({
    description: '',
    location: '',
    websiteURL: '',
    contactEmail: '',
    socialMediaLinks: [''],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchWrapperRef = useRef(useFetchWrapper());
  const t = useT();

  useEffect(() => {
    if (band) {
      setBandData({
        description: band.Description || '',
        location: band.Location || '',
        websiteURL: band.WebsiteURL || '',
        contactEmail: band.ContactEmail || '',
        socialMediaLinks: band.SocialMediaLinks && band.SocialMediaLinks.length > 0 ? band.SocialMediaLinks : [''],
      });
    }
  }, [band]);

  const handleAddSocialMediaLink = () => {
    setBandData(prev => ({
      ...prev,
      socialMediaLinks: [...prev.socialMediaLinks, '']
    }));
  };

  const handleRemoveSocialMediaLink = (index) => {
    setBandData(prev => ({
      ...prev,
      socialMediaLinks: prev.socialMediaLinks.filter((_, i) => i !== index)
    }));
  };

  const handleSocialMediaLinkChange = (index, value) => {
    setBandData(prev => {
      const newLinks = [...prev.socialMediaLinks];
      newLinks[index] = value;
      return {
        ...prev,
        socialMediaLinks: newLinks
      };
    });
  };

  const handleInputChange = (field, value) => {
    setBandData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // The textarea's `required` lets whitespace through (BAND-03, ENG-523).
    const description = bandData.description.trim();
    if (!description) {
      setError(t('bandSettings.errors.descriptionRequired'));
      return;
    }

    // Same allowlist the backend enforces, checked here so the member is told
    // which link is the problem before the request is made.
    const socialMediaLinkError = findSocialMediaLinkError(bandData.socialMediaLinks, t);
    if (socialMediaLinkError) {
      setError(socialMediaLinkError);
      return;
    }

    // Send links trimmed and with the https:// people leave off, dropping
    // empty rows (stored rows may hold nulls)
    const filteredSocialMediaLinks = bandData.socialMediaLinks.map(normalizeSocialMediaUrl).filter(Boolean);

    try {
      setLoading(true);

      // Transform field names to match API expected format
      const updateData = {
        description,
        location: bandData.location,
        website_url: bandData.websiteURL,
        contact_email: bandData.contactEmail,
        social_media_links: filteredSocialMediaLinks,
      };

      await fetchWrapperRef.current.put(`${config.apiBaseUrl}/bands/${band.ID || band.id}`, updateData);
      
      setSuccessMessage(t('bandSettings.success.updated'));
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Error updating band data:', error);
      // Show the server's reason (e.g. which link it rejected) when the body
      // carried one. fetchWrapper substitutes the status text when it did
      // not, and a bare "Bad Request" helps nobody, so fall back to ours.
      const serverMessage = error?.message && error.message !== error.statusText ? error.message : '';
      setError(serverMessage || t('bandSettings.errors.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="band-settings-content">
      <div className="band-settings-header">
        <button onClick={onBack} className="band-settings-back-button">
          <FaArrowLeft className="rtl-mirror" /> {t('common.actions.back')}
        </button>
        <h2 className="band-settings-title">{band?.Name || t('bandSettings.fallbackTitle')}</h2>
      </div>
      
      <div className="band-settings-content">
        {error && (
          <div className="message error-message">
            <FaTimes className="message-icon" />
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="message success-message">
            ✓ {successMessage}
          </div>
        )}
        
        {/* Saved on its own, as soon as it is flipped (ENG-665). */}
        {(band?.ID || band?.id) && <FreeRequestsToggle bandId={band.ID || band.id} />}

        <form className="band-settings-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <label className="form-label">
              {t('bandSettings.form.descriptionLabel')}
              <textarea
                value={bandData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="form-input"
                placeholder={t('bandSettings.form.descriptionPlaceholder')}
                rows="4"
                required
              />
            </label>
          </div>
          
          <div className="form-section">
            <label className="form-label">
              {t('bandSettings.form.locationLabel')}
              <input
                type="text"
                value={bandData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="form-input"
                placeholder={t('bandSettings.form.locationPlaceholder')}
                required
              />
            </label>
          </div>
          
          <div className="form-section">
            <label className="form-label">
              {t('bandSettings.form.websiteLabel')}
              <input
                type="url"
                value={bandData.websiteURL}
                onChange={(e) => handleInputChange('websiteURL', e.target.value)}
                className="form-input"
                placeholder={t('bandSettings.form.websitePlaceholder')}
              />
            </label>
          </div>
          
          <div className="form-section">
            <label className="form-label">
              {t('bandSettings.form.contactEmailLabel')}
              <input
                type="email"
                value={bandData.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                className="form-input"
                placeholder={t('bandSettings.form.contactEmailPlaceholder')}
                required
              />
            </label>
          </div>
          
          <div className="form-section">
            <label className="form-label">{t('bandSettings.form.socialMediaLinksLabel')}</label>
            <div className="social-media-links-container">
              {/* type="text", not "url": the browser's url check would refuse
                  a pasted "instagram.com/theband" before handleSubmit can add
                  the https:// (and it lets javascript: through anyway); the
                  allowlist check is the real validation. */}
              {bandData.socialMediaLinks.map((link, index) => (
                <div key={index} className="social-media-link-item">
                  <input
                    type="text"
                    inputMode="url"
                    value={link}
                    onChange={(e) => handleSocialMediaLinkChange(index, e.target.value)}
                    className="form-input social-media-input"
                    placeholder={t('bandSettings.form.socialMediaLinkPlaceholder')}
                  />
                  {bandData.socialMediaLinks.length > 1 && (
                    <button 
                      type="button" 
                      onClick={() => handleRemoveSocialMediaLink(index)}
                      className="small-button remove-button"
                      title={t('bandSettings.form.removeLinkTitle')}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              ))}
              <button 
                type="button" 
                onClick={handleAddSocialMediaLink} 
                className="small-button add-button"
              >
                <FaPlus /> {t('bandSettings.form.addSocialMediaLink')}
              </button>
            </div>
          </div>
          
          <div className="form-buttons">
            <button 
              type="button" 
              className="form-button cancel-button" 
              onClick={onBack}
              disabled={loading}
            >
              <FaTimes /> {t('common.actions.cancel')}
            </button>

            {loading ? (
              <LoadingSpinner />
            ) : (
              <button type="submit" className="form-button save-button">
                <FaSave /> {t('bandSettings.actions.saveChanges')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default BandSettingsForm;